
import { i18nManager } from "../i18n";
import { LzEncryptor } from "./Encryptor";
import { IProfile, SanitizedResponse } from "../services";
import { MessageSchemesIcons, NoseurObject, Scheme, Toaster } from "@rivtn/noseur";

export const Utils = {

    BUTTON_LOADING_PROPS: {
        text: "",
        disabled: true,
        leftIcon: "fas fa-spinner fa-pulse"
    },

    COMPOSED_PASSWORD_PROGRESS_PROPS: {
        percentageColors: {
            0: {
                fg: "#EB5959",
            },
            25: {
                fg: "#FFDD85",
            },
            80: {
                fg: "#1BA6C5",
            },
            90: {
                fg: "#0FA883",
            },
        }
    },

    CIRCLE: (<svg width="13" height="13" viewBox="0 0 13 13" fill="none" xmlns="http://www.w3.org/2000/svg">
        <ellipse cx="6.33369" cy="6.5" rx="6.33369" ry="6.5" fill="#244147" />
    </svg>
    ),

    collapseString: (value: string, max: number, contd: string = "...") => {
        let collapsedBack = "";
        let collapsedFront = "";
        let halfMax = Math.trunc(max / 2);
        if (!value || value?.length < max) return value;
        for (let index = 0; index < halfMax; index++) {
            collapsedFront += value[index];
            collapsedBack += value[value?.length - halfMax + index];
        }
        return collapsedFront + contd + collapsedBack;
    },

    report: (content: string, scheme: Scheme = Scheme.DANGER, icon: MessageSchemesIcons = MessageSchemesIcons.INFO, lifetime: number = 5000) => {
        Toaster.toast({
            icon,
            scheme,
            content,
            lifetime,
            foreScheme: true,
            showProgressbar: true,
            pauseDelayOnHover: true,
            style: { background: "white" },
        });
    },

    reportSuccess: (content: string, lifetime: number = 5000) => {
        Utils.report(content, Scheme.SUCCESS, MessageSchemesIcons.SUCCESS, lifetime);
    },

    reportError: (response: SanitizedResponse<any> | string) => {
        Utils.report(((typeof response === "string") ? response : (response?.errorMessage ?? `${response}`)));
    },

    profilesWithProfile: (profiles: IProfile[], profile: IProfile) => {
        if (profiles?.findIndex((profile) => profile.session_token === profile.session_token) < 0) {
            profiles.push(profile);
        }
        return profiles ?? [];
    },

    selectProfile: (profiles: IProfile[], externalId: string) => {
        return profiles.find((profile) => profile.external_id === externalId);
    },

    redirectToReferrer: async (url: string, profile: IProfile, isOauth: boolean = true, postMessageOnly?: boolean, isTrusted?: boolean) => {
        if (isOauth && !((url.includes(window.location.host) || url.startsWith("/")) && url.includes("/oauth"))) {
            url = `/oauth?ref=${url}`;
        }
        if (isOauth) {
            Utils.reportSuccess(i18nManager.Labels.sign_in.sign_in_success);
        } else {
            Utils.reportSuccess(i18nManager.Labels.oauth.auth_success.replace("${location}", url.substring(0, 30)));
        }
        if (isOauth) {
            window.location.href = url;
            return;
        }
        const { access_token, session_token, ...details } = profile;
        const separator = url?.includes("?") ? "&" : "?";
        const auth: any = {
            p: details,
            a: access_token,
        };
        if (isTrusted) auth.s = session_token;
        Utils.postMessage("AUTHENTICATED", LzEncryptor.encrypt(JSON.stringify(auth), true));
        if (postMessageOnly) return;
        window.location.href = url + separator + `pas=${encodeURIComponent(LzEncryptor.encrypt(JSON.stringify(auth), true))}`;
    },

    extractDomain: (url: string) => {
        let domain = url;
        if (domain.startsWith("http://")) domain = domain.substring(7);
        else if (domain.startsWith("https://")) domain = domain.substring(8);
        domain = (domain.includes("#") ? domain.substring(0, domain.indexOf("#")) : domain);
        const slashIndex = domain.indexOf("/");
        domain = domain.substring(0, slashIndex > -1 ? slashIndex : domain.length);
        return domain.trim();
    },

    __TRUSTED_DOMAINS_PROCESSED: false,
    __CACHED_TRUSTED_SCHEMES_BIDS: {} as NoseurObject<string>,
    __CACHED_TRUSTED_DOMAINS_SCHEMES: {} as NoseurObject<string>,
    __CACHED_TRUSTED_DOMAINS_FINE_NAMES: {} as NoseurObject<string>,

    extractTrustedDomainData(referrer: string): [string, NoseurObject<string>, NoseurObject<string>] {
        const referrerDomain = Utils.extractDomain(referrer);
        if (!Utils.__TRUSTED_DOMAINS_PROCESSED) {
            const trustedConfiguration = JSON.parse(process.env.REACT_APP_OAUTH_TRUSTED_CONFIGURATION ?? "");
            for (const trusted of trustedConfiguration) {
                if (!trusted.id) {
                    trusted.id = "";
                }
                if (trusted.bid && trusted.id) {
                    Utils.__CACHED_TRUSTED_SCHEMES_BIDS[trusted.id] = trusted.bid;
                }
                if (trusted.name && trusted.id) {
                    Utils.__CACHED_TRUSTED_DOMAINS_FINE_NAMES[trusted.id] = trusted.name;
                }
                const domainsAndApps = (trusted.apps ?? []).concat(trusted.domains ?? []);
                for (const domainOrApp of domainsAndApps) {
                    Utils.__CACHED_TRUSTED_DOMAINS_SCHEMES[domainOrApp.trim()] = trusted.id;
                }
            }
            Utils.__TRUSTED_DOMAINS_PROCESSED = true;
        }
        return [referrerDomain, Utils.__CACHED_TRUSTED_DOMAINS_SCHEMES, Utils.__CACHED_TRUSTED_SCHEMES_BIDS];
    },

    getReferrerFineName(scheme: string, referrer: string) {
        return Utils.__CACHED_TRUSTED_DOMAINS_FINE_NAMES[scheme] ?? referrer;
    },

    postMessage(type: string, message: string) {
        window?.postMessage?.({ type, message });
    },

}

export const Schemes: {
    RIVTN_PATULCIUS: Scheme;
} = {
    RIVTN_PATULCIUS: Scheme.DARK
}

export * from "./Debugger";
export * from "./Encryptor";
export * from "./CacheManager";